import { useState } from "react";
import { Link, useParams } from "react-router";
import { Button } from "../../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { api } from "../../../lib/api";
import { getErrorMessage } from "../../../lib/laravelErrors";
import { useAuth } from "../../../providers/AuthProvider";
import { ReceiveItemsModal } from "../components/ReceiveItemsModal";
import { StatusTimeline } from "../components/StatusTimeline";
import { usePurchaseOrder } from "../hooks/usePurchaseOrder";

export function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const { po, loading, error, reload } = usePurchaseOrder(id);
  const [actionError, setActionError] = useState<string | null>(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function setStatus(next: string) {
    if (!token || !id) return;
    setActionError(null);
    setBusy(true);
    try {
      await api.purchaseOrders.updatePOStatus({ token, id, status: next });
      await reload();
    } catch (e) {
      setActionError(getErrorMessage(e, "Status update failed"));
    } finally {
      setBusy(false);
    }
  }

  if (!id) {
    return <div className="p-6 text-destructive">Missing purchase order id.</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-1">Purchase order #{id}</h1>
          <p className="text-muted-foreground">{po?.dealer.name ?? "—"}</p>
        </div>
        <div className="flex gap-2">
          {po?.status === "DRAFT" ? (
            <Button asChild variant="outline">
              <Link to={`/purchase-orders/${id}/edit`}>Edit</Link>
            </Button>
          ) : null}
          <Button asChild variant="outline">
            <Link to="/purchase-orders">Back</Link>
          </Button>
        </div>
      </div>

      {error ? <div className="text-destructive text-sm">{error}</div> : null}
      {actionError ? <div className="text-destructive text-sm">{actionError}</div> : null}

      {loading || !po ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : (
        <>
          <StatusTimeline status={po.status} />

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Actions</CardTitle>
              <CardDescription>Workflow: Draft → Sent → Confirmed → Receive</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {po.status === "DRAFT" ? (
                <>
                  <Button disabled={busy} onClick={() => void setStatus("SENT")}>
                    Send PO
                  </Button>
                  <Button variant="destructive" disabled={busy} onClick={() => void setStatus("CANCELLED")}>
                    Cancel
                  </Button>
                </>
              ) : null}
              {po.status === "SENT" ? (
                <>
                  <Button disabled={busy} onClick={() => void setStatus("CONFIRMED")}>
                    Confirm PO
                  </Button>
                  <Button variant="destructive" disabled={busy} onClick={() => void setStatus("CANCELLED")}>
                    Cancel
                  </Button>
                </>
              ) : null}
              {po.status === "CONFIRMED" || po.status === "PARTIALLY_RECEIVED" ? (
                <Button disabled={busy} onClick={() => setReceiveOpen(true)}>
                  Receive items
                </Button>
              ) : null}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Line items</CardTitle>
              <CardDescription>Ordered vs received quantities</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right">Purchase price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {po.items.map((it) => (
                    <TableRow key={it.product_id}>
                      <TableCell>{it.product_name || it.product_id}</TableCell>
                      <TableCell className="text-right">{it.ordered_qty}</TableCell>
                      <TableCell className="text-right">{it.received_qty}</TableCell>
                      <TableCell className="text-right">{it.purchase_price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <ReceiveItemsModal
            open={receiveOpen}
            onOpenChange={setReceiveOpen}
            purchaseOrderId={id}
            lines={po.items.map((it) => ({
              product_id: it.product_id,
              product_name: it.product_name,
              ordered_qty: it.ordered_qty,
              received_qty: it.received_qty,
              purchase_price: it.purchase_price,
            }))}
            onDone={() => void reload()}
          />
        </>
      )}
    </div>
  );
}
